// Pure helpers for the full-page sale-order (PO) editor (MtSaleOrderEdit.vue).
// Vue-free so jest can test them without mounting a .vue.

// Recursively sort object keys so two states with the same content serialize
// to the same string whatever order their keys were written in.
function sortKeys(v) {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === "object") {
    const out = {};
    for (const k of Object.keys(v).sort()) out[k] = sortKeys(v[k]);
    return out;
  }
  return v === undefined ? null : v;
}

// Stable JSON of an editor state. The caller decides what goes into the state
// (header fields + items as {product_id, qty, discount} + a file marker).
export function snapshot(state) {
  return JSON.stringify(sortKeys(state === undefined ? null : state));
}

// true when `current` differs from the `saved` snapshot. Either side may be a
// state object or a snapshot string.
export function isDirty(saved, current) {
  const a = typeof saved === "string" ? saved : snapshot(saved);
  const b = typeof current === "string" ? current : snapshot(current);
  return a !== b;
}

// Add `product` (a products-picker row) at the previewed `price`
// ({retail_price, unit_price, source}). If the product is already on the
// order, its qty goes up by one instead. Returns a new array.
export function addLine(lines, product, price) {
  const list = Array.isArray(lines) ? lines : [];
  const p = product || {};
  const pr = price || {};
  const idx = list.findIndex(l => l.product_id === p.id);
  if (idx >= 0) {
    return list.map((l, i) => (i === idx ? { ...l, qty: (Number(l.qty) || 0) + 1 } : l));
  }
  return list.concat([
    {
      product_id: p.id,
      product_code: p.code || "",
      product_name: p.name || "",
      barcode: p.barcode || "",
      man_pn: p.man_pn || "",
      wms_linked: p.wms_linked,
      qty: 1,
      discount: 0,
      retail_price: Number(pr.retail_price) || 0,
      unit_price: Number(pr.unit_price) || 0,
      price_source: pr.source || "default",
      no_price: false,
    },
  ]);
}

// Set the qty of line `idx` (floored to a whole unit). A qty below 1 — or
// anything that is not a number — removes the line. Returns a new array.
export function setQty(lines, idx, qty) {
  const list = Array.isArray(lines) ? lines : [];
  if (idx < 0 || idx >= list.length) return list.slice();
  const n = Math.floor(Number(qty));
  if (!(n >= 1)) return list.filter((_, i) => i !== idx);
  return list.map((l, i) => (i === idx ? { ...l, qty: n } : l));
}

// Sale-order save/confirm error code (go-core Msg) → i18n key, shared by the
// PO editor and the SO detail "confirm" button. null = show the BE Cause.
export function saleOrderErrorKey(code) {
  switch (code) {
    case "missing_po_number": return "modern_trade.sale_order.err_missing_po_number";
    case "missing_po_file": return "modern_trade.sale_order.err_missing_po_file";
    case "no_items": return "modern_trade.sale_order.err_no_items";
    case "not_draft": return "modern_trade.sale_order.edit_draft_only";
    default: return null;
  }
}

// Route param :id → positive whole number, or null (e.g. /sale-orders/abc/edit).
export function parseSaleOrderId(v) {
  const s = String(v == null ? "" : v).trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n > 0 && Number.isSafeInteger(n) ? n : null;
}
